// Web adapter over the brain's KV-backed device-local stores (docs/09 §10,
// wave B3-web): KvFavorites/KvAliases/KvCallLog are now the ONE
// implementation of favorites/aliases/call-log shared with iOS and Android
// (frontend/brain/src/runtime/{favorites,aliases,callLog}.ts) — the web no
// longer keeps its own copy in `storage/{favorites,aliases,callLog}.ts`
// (those stay only as the one-time legacy-localStorage migration source).
//
// Screens read through `useSyncExternalStore`-style sync getters/subscribers
// (the SAME call-site shapes the old localStorage modules had), but the brain
// stores are async (IndexedDB), so this module keeps an in-memory MIRROR per
// store: seeded once `initKvStores()` (legacy import + `store.load()`)
// finishes, referentially stable between writes, and updated AFTER a write
// resolves — KV is the truth, the mirror follows it (never the reverse).
// Before the mirror is seeded, reads return empty: the same "nothing yet" a
// fresh device would show, no loading spinner (docs/09 boot note).
//
// `initKvStores()` is kicked off fire-and-forget by
// `shell/accountContext.tsx`'s `getKv()` — the earliest point the app
// resolves its on-device KV — so it starts before the authenticated screens
// that read these stores ever mount, without delaying first paint.

import { KvFavorites } from '@merkury/brain/runtime/favorites.js';
import { KvAliases } from '@merkury/brain/runtime/aliases.js';
import { KvCallLog } from '@merkury/brain/runtime/callLog.js';
import type { EndedCallState } from '@merkury/brain/call/types.js';
import { MemoryKvStore, openIdbKv, type KvStore } from '@merkury/brain/storage/kv.js';
import { migrateLegacyLocalStorage } from '@merkury/brain/storage/migrate.js';
import { isCapacitorNative } from '../shell/capacitor/platform.js';
import { getCachedFriends } from './serverCache.js';
// The app's own zod-derived type (storage/callLog.ts), not the brain's plain
// interface: both describe the same wire shape, but under
// `exactOptionalPropertyTypes` the zod-inferred `peerName?: string |
// undefined` is the more permissive of the two — every value the brain's
// `KvCallLog` ever produces (which only OMITS the key, never sets it to
// `undefined`) satisfies it, and this is the type every screen/test already
// imports (`../../storage/callLog.js` re-exported below).
import type { StoredCallLogEntry } from './callLog.js';

export type { StoredCallLogEntry };

type Listener = () => void;

/** `lastSyncedAt` marker for the E2E alias cloud sync (docs/08 §8.3) — same
 *  slot name the brain (runtime/core.ts) uses on iOS/Android; wiped together
 *  with the alias map on sign-out. */
const ALIAS_SYNC_MARKER_KEY = 'merkury.aliases.sync.v1';

// --- device KV resolution ------------------------------------------------------

/**
 * The on-device KV connection: Capacitor Keystore-backed on native,
 * IndexedDB in the browser. Falls back to an in-memory store when neither is
 * available (jsdom under vitest has no IndexedDB) — the same graceful
 * degradation `storage/favorites.ts`'s `ls()` uses for a disabled
 * `localStorage`. Not memoized here — the caller (`accountContext.getKv()`)
 * owns the single shared instance; this is just the resolution logic so it
 * lives in exactly one place.
 */
export async function resolveDeviceKv(): Promise<KvStore> {
  if (isCapacitorNative()) {
    return (await import('../shell/capacitor/index.js')).createNativeKv();
  }
  if (typeof indexedDB !== 'undefined') return openIdbKv();
  return new MemoryKvStore();
}

// --- module state ----------------------------------------------------------

let favoritesStore: KvFavorites | null = null;
let aliasesStore: KvAliases | null = null;
let callLogStore: KvCallLog | null = null;
let kv: KvStore | null = null;

let favoritesMirror: readonly string[] = [];
let aliasesMirror: Readonly<Record<string, string>> = {};
let callLogMirror: readonly StoredCallLogEntry[] = [];

const favoritesListeners = new Set<Listener>();
const aliasesListeners = new Set<Listener>();
const callLogListeners = new Set<Listener>();

function notifyFavorites(): void {
  for (const listener of favoritesListeners) listener();
}
function notifyAliases(): void {
  for (const listener of aliasesListeners) listener();
}
function notifyCallLog(): void {
  for (const listener of callLogListeners) listener();
}

let lastKv: KvStore | null = null;
let readyPromise: Promise<void> | null = null;
/** Fallback used only when nothing ever called `initKvStores()` explicitly
 *  (a focused unit test touching a getter/setter directly). */
let lazyInit: Promise<void> | null = null;

/**
 * One-time boot wiring (docs/09 §10): import any legacy localStorage data
 * into `store`, construct the shared brain stores over it, and hydrate the
 * mirrors. Calling again with the SAME `store` instance is a no-op (returns
 * the original promise); calling with a DIFFERENT instance (tests rendering
 * a fresh `MemoryKvStore` per case) starts a clean cycle — a still-resolving
 * previous cycle drops its result instead of clobbering the new one.
 */
export function initKvStores(store: KvStore): Promise<void> {
  if (store === lastKv && readyPromise) return readyPromise;
  lastKv = store;
  const promise = (async (): Promise<void> => {
    try {
      await migrateLegacyLocalStorage(store, localStorage);
    } catch {
      // Best-effort — a failed import must not stop the app from serving.
    }
    if (lastKv !== store) return; // superseded mid-migration — drop this cycle
    const favorites = new KvFavorites(store);
    const aliases = new KvAliases(store);
    const callLog = new KvCallLog(store, (userId) => resolvePeerName(userId));
    const [f, a] = await Promise.all([favorites.load(), aliases.load()]);
    const c = await callLog.list(); // also backfills+persists peer names once
    if (lastKv !== store) return; // superseded mid-load — drop this cycle
    kv = store;
    favoritesStore = favorites;
    aliasesStore = aliases;
    callLogStore = callLog;
    favoritesMirror = f;
    aliasesMirror = a;
    callLogMirror = c;
    notifyFavorites();
    notifyAliases();
    notifyCallLog();
  })();
  readyPromise = promise;
  return promise;
}

/** The REAL (never aliased) display name from the friends cache — mirrors
 *  storage/callLog.ts `nameOf`. Aliasing is applied at render time
 *  (screens/ui/peerName.ts), never snapshotted into the log entry. */
function resolvePeerName(userId: string): string | undefined {
  return getCachedFriends()?.data.find((f) => f.userId === userId)?.displayName;
}

function ensureReady(): Promise<void> {
  if (readyPromise) return readyPromise;
  lazyInit ??= resolveDeviceKv().then((store) => initKvStores(store));
  return lazyInit;
}

/**
 * Test seam: the KV instance this module is using — resolving it lazily
 * (`ensureReady()`) if nothing has called `initKvStores()` yet. Lets test
 * harnesses that seed favorites/aliases/call-log data BEFORE rendering
 * (`screens/testUtils/renderScreen.tsx`) hand the SAME store to
 * `<AccountProvider kv={…}>`, so its own `getKv()` → `initKvStores()` call
 * is a no-op (same instance) instead of clobbering the just-seeded mirror
 * with a second, empty store — mirrors production, where both always share
 * one KV.
 */
export async function testKv(): Promise<KvStore> {
  await ensureReady();
  // `ensureReady()` only resolves once `kv` is set (see `initKvStores`).
  return kv as KvStore;
}

/** Test seam: drop every store/mirror so the next call re-initializes from
 *  scratch (mirrors the old modules' `reset…Cache()` hooks, scoped to all
 *  three stores at once since they now share one boot sequence). */
export function resetKvStoresForTest(): void {
  favoritesStore = null;
  aliasesStore = null;
  callLogStore = null;
  kv = null;
  favoritesMirror = [];
  aliasesMirror = {};
  callLogMirror = [];
  lastKv = null;
  readyPromise = null;
  lazyInit = null;
}

// --- favorites ---------------------------------------------------------------

/** Subscribe to favorite-set changes (for `useSyncExternalStore`). */
export function subscribeFavorites(listener: Listener): () => void {
  favoritesListeners.add(listener);
  return () => {
    favoritesListeners.delete(listener);
  };
}

/** The current favorite userIds. Referentially stable until the set changes;
 *  empty before `initKvStores()` has hydrated the mirror. */
export function getFavorites(): readonly string[] {
  return favoritesMirror;
}

export function isFavorite(userId: string): boolean {
  return favoritesMirror.includes(userId);
}

/** Add or remove `userId` from favorites; no-op if already in the wanted
 *  state. KV-first: the store is written, then the mirror follows. */
export async function setFavorite(userId: string, favorite: boolean): Promise<void> {
  await ensureReady();
  if (!favoritesStore) return;
  const next = await favoritesStore.set(userId, favorite);
  if (next === favoritesMirror) return; // no-op — store already had this value
  favoritesMirror = next;
  notifyFavorites();
}

/** Flip `userId`'s favorite state; returns the new state. */
export async function toggleFavorite(userId: string): Promise<boolean> {
  await ensureReady();
  const next = !isFavorite(userId);
  await setFavorite(userId, next);
  return next;
}

/** Wipe all favorites — sign-out must not leave the previous account's stars. */
export async function clearFavorites(): Promise<void> {
  await ensureReady();
  await favoritesStore?.clear();
  favoritesMirror = [];
  notifyFavorites();
}

// --- aliases -------------------------------------------------------------------

/** Subscribe to alias-map changes (for `useSyncExternalStore`). */
export function onAliasesChanged(listener: Listener): () => void {
  aliasesListeners.add(listener);
  return () => {
    aliasesListeners.delete(listener);
  };
}

/** The current alias map. Referentially stable until it changes; empty
 *  before `initKvStores()` has hydrated the mirror. */
export function getAliases(): Readonly<Record<string, string>> {
  return aliasesMirror;
}

export function getAlias(userId: string): string | undefined {
  return aliasesMirror[userId];
}

/**
 * Set (or clear) `userId`'s alias. Trimmed + sanitized (docs/08 §3) by the
 * brain store; a null/empty/all-whitespace value CLEARS it. Returns the
 * stored alias, or null if cleared. KV-first: the store is written, then the
 * mirror follows.
 */
export async function setAlias(userId: string, alias: string | null): Promise<string | null> {
  await ensureReady();
  if (!aliasesStore) return null;
  const result = await aliasesStore.set(userId, alias);
  const next = aliasesStore.get();
  if (next !== aliasesMirror) {
    aliasesMirror = next;
    notifyAliases();
  }
  return result;
}

/** Replace the WHOLE alias map (docs/08 §8.3 cloud sync pull). Always
 *  notifies — the caller (AliasSyncDriver) only calls this on a real change. */
export async function replaceAliases(map: Record<string, string>): Promise<void> {
  await ensureReady();
  if (!aliasesStore) return;
  await aliasesStore.replaceAll(map);
  aliasesMirror = aliasesStore.get();
  notifyAliases();
}

/** The stored `lastSyncedAt` marker, or null before the first sync. */
export async function getAliasSyncMarker(): Promise<string | null> {
  await ensureReady();
  if (!kv) return null;
  return (await kv.get<string>(ALIAS_SYNC_MARKER_KEY).catch(() => undefined)) ?? null;
}

/** Record the server `updatedAt` this device last reconciled with (docs/08 §8.3). */
export async function setAliasSyncMarker(updatedAt: string): Promise<void> {
  await ensureReady();
  await kv?.put(ALIAS_SYNC_MARKER_KEY, updatedAt);
}

/** Wipe all aliases — sign-out must not leave the previous account's labels.
 *  The sync marker goes with them so the next account re-pulls from scratch. */
export async function clearAliases(): Promise<void> {
  await ensureReady();
  await aliasesStore?.clear();
  await kv?.delete(ALIAS_SYNC_MARKER_KEY).catch(() => undefined);
  aliasesMirror = {};
  notifyAliases();
}

// --- call log --------------------------------------------------------------------

/** Subscribe to log changes (for `useSyncExternalStore`). */
export function subscribeCallLog(listener: Listener): () => void {
  callLogListeners.add(listener);
  return () => {
    callLogListeners.delete(listener);
  };
}

/** The call log, newest first. Referentially stable until it changes; empty
 *  before `initKvStores()` has hydrated the mirror. */
export function getCallLog(): readonly StoredCallLogEntry[] {
  return callLogMirror;
}

/**
 * Append the engine's ended call to the log. Idempotent per callId — a
 * double-fired subscriber must not duplicate history. Returns the entry, or
 * undefined when nothing is logged (answered elsewhere / duplicate).
 */
export async function recordEndedCall(
  ended: EndedCallState,
): Promise<StoredCallLogEntry | undefined> {
  await ensureReady();
  if (!callLogStore) return undefined;
  const entry = await callLogStore.record(ended);
  if (entry) {
    callLogMirror = await callLogStore.list();
    notifyCallLog();
  }
  return entry;
}

/** How many incoming missed calls are still unseen — the in-app Recents badge
 *  and the native launcher badge both read this. */
export function getUnseenMissedCount(): number {
  return callLogMirror.filter((e) => e.outcome === 'missed' && !e.seen).length;
}

/** Clear the missed-call badge: mark every missed entry seen. */
export async function markAllMissedSeen(): Promise<void> {
  await ensureReady();
  if (!callLogStore) return;
  await callLogStore.markAllMissedSeen();
  const next = await callLogStore.list();
  if (next === callLogMirror) return; // nothing was unseen — no notify
  callLogMirror = next;
  notifyCallLog();
}

/** Wipe the log — sign-out must not leave the previous account's history. */
export async function clearCallLog(): Promise<void> {
  await ensureReady();
  await callLogStore?.clear();
  callLogMirror = [];
  notifyCallLog();
}

// --- lifecycle -----------------------------------------------------------------

/**
 * Wipe every store — sign-out/delete must not leave the previous account's
 * favorites/aliases/call-log behind. Mirrors the brain's own teardown for
 * iOS/Android (runtime/core.ts `teardownSession`): the underlying KV was
 * already cleared by `AccountStore.wipe()`'s `kv.clear()` by the time this
 * runs, so this just resets the in-memory mirrors (a process-lifetime
 * singleton the KV clear alone would not touch) — same store instances, same
 * KV connection, ready for the next account to write into.
 */
export async function clearKvStores(): Promise<void> {
  await Promise.all([clearFavorites(), clearAliases(), clearCallLog()]);
}
